import { Typography } from '@mui/material'
import Button from '@mui/material/Button'
import TextField from '@mui/material/TextField'
import MenuItem from '@mui/material/MenuItem'
import { FormikErrors, useFormik } from 'formik'
import React from 'react'
import { t } from '../../locale'
import { CreateControlField, Control } from '../../types'
import {
	categories,
	categoryStr,
	isBoolMachineCategory,
	isDenjiEnabledHouse,
	isDenjiMachineCategory,
	isHpMachineCategory,
	isProtoHouse,
	isTwayMachineCategory,
} from '../../utils'
import { CategoryIcon } from '../MachineTable/MachineIcon'
import { pinValidate } from '../../utils/businessUtil'
import TwayForm from '../TwayForm'
import DenjiForm from '../DenjiForm'
import { PinIndicator, getUsedPins } from './PinIndicator'

import { House } from '../../types'

type Fields = Omit<CreateControlField, 'houseId'>
type Props = {
	disabled: boolean
	handleSubmit: (fields: Fields) => void
	house?: House
	availableHps?: Control[]
	existingControls?: Control[]
}

function CreateControlForm(props: Props) {
	const { usedPins, twayPins } = getUsedPins(props.existingControls || [])

	const {
		values,
		errors,
		touched,
		handleChange,
		handleSubmit,
		setFieldTouched,
		setFieldValue,
	} = useFormik<Fields>({
		initialValues: {
			category: 'curtain',
			label: categoryStr['curtain'], // 既定で機器タイプ名を入れる
			activePin: null,
			negativePin: null,
			timer: 0,
			cooltime: 0,
			pumpNumber: null,
			relatedHpId: null,
		},
		validate: (values) => {
			const errors: FormikErrors<Fields> = {}

			if (!values.label) {
				errors.label = '必須です。'
			}

			// HP機器はピンを持たないためピンバリデーションをスキップ
			if (isHpMachineCategory(values.category)) {
				if (!values.pumpNumber || values.pumpNumber <= 0) {
					errors.pumpNumber = 'ポンプ番号を入力してください（正の整数）。'
				}
			} else {
				if (pinValidate(values.activePin)) {
					errors.activePin = '1から16の値にしてください。'
				} else if (pinValidate(values.negativePin)) {
					errors.negativePin = '1から16の値にしてください。'
				} else if (!values.activePin && !values.negativePin) {
					errors.activePin =
						'開けるピンか閉めるピンを少なくとも1つ有効にしてください。'
				}
			}

			return errors
		},
		onSubmit: (fields: Fields) => {
			if (isBoolMachineCategory(fields.category)) {
				fields.timer = null
			}
			if (isTwayMachineCategory(fields.category)) {
				fields.timer = 0
			}
			if (isHpMachineCategory(fields.category)) {
				fields.activePin = null
				fields.negativePin = null
			}
			props.handleSubmit(fields)
		},
	})
	const change = (name: keyof Fields, e: React.ChangeEvent<unknown>) => {
		e.persist()
		handleChange(e)
		setFieldTouched(name, true, false)
	}
	const isBool = isBoolMachineCategory(values.category)
	const isHp = isHpMachineCategory(values.category)
	const isTway = isTwayMachineCategory(values.category)
	const isDenji = isDenjiMachineCategory(values.category)

	const hpControls =
		props.availableHps?.filter((c) => c.category === 'hp') || []

	return (
		<div>
			<Typography>{t.control.base}を作成</Typography>
			<form style={{ display: 'grid', gap: '8px' }}>
				<div>
					<Typography>機器タイプ</Typography>
					<div
						style={{
							display: 'grid',
							gridTemplateColumns: 'repeat(9, 1fr)',
						}}
					>
						{categories
							.filter((category) => {
								// hp と tway は isProtoHouse の場合のみ表示
								if (
									(category === 'hp' || category === 'tway') &&
									(!props.house || !isProtoHouse(props.house.id))
								) {
									return false
								}
								// 電磁弁は許可ハウス（ひとまず proto と同じ）のみ表示
								if (
									category === 'denji' &&
									!isDenjiEnabledHouse(props.house?.id)
								) {
									return false
								}
								return true
							})
							.map((category) => {
								return (
									<Button
										key={category}
										onClick={() => {
											// ラベルが未入力 or 前タイプの既定名のままなら、新タイプ名を既定入力
											if (
												!values.label ||
												values.label === categoryStr[values.category]
											) {
												setFieldValue('label', categoryStr[category])
											}
											setFieldValue('category', category)
											// Twayの場合はtimerを0に自動設定
											if (isTwayMachineCategory(category)) {
												setFieldValue('timer', 0)
											}
										}}
										style={{
											height: '4rem',
											padding: '0',
											paddingBottom: '8px',
											textAlign: 'center',
											borderBottomLeftRadius: '0',
											borderBottomRightRadius: '0',
											...(category === values.category
												? {
														border: 'solid 1px black',
														borderBottom: 'none',
												  }
												: { borderBottom: 'solid 1px black' }),
										}}
									>
										<div style={{ display: 'grid' }}>
											<CategoryIcon
												category={category}
												stepRate={6}
												style={{
													height: '1.8rem',
													fontSize: '1.8rem',
												}}
											/>
											<Typography
												style={{
													fontSize: '0.6rem',
												}}
											>
												{categoryStr[category]}
											</Typography>
										</div>
									</Button>
								)
							})}
					</div>
				</div>
				{isTway ? (
					<TwayForm
						disabled={props.disabled}
						house={props.house}
						existingControls={props.existingControls}
						handleSubmit={(fields) => {
							props.handleSubmit({
								category: 'tway',
								label: fields.label,
								activePin: fields.activePin,
								negativePin: fields.negativePin,
								timer: fields.timer,
								cooltime: 0,
								relatedHpId: fields.relatedHpId,
							})
						}}
						availableHps={
							hpControls.map((hp) => ({ id: hp.id, label: hp.label })) || []
						}
					/>
				) : isDenji ? (
					<DenjiForm
						disabled={props.disabled}
						house={props.house}
						existingControls={props.existingControls}
						handleSubmit={(fields) => {
							props.handleSubmit({
								category: 'denji',
								label: fields.label,
								activePin: fields.activePin,
								negativePin: null,
								timer: null,
								cooltime: 0,
							})
						}}
					/>
				) : (
					<>
						<TextField
							name="label"
							type="text"
							autoComplete="pf-label"
							error={touched.label && Boolean(errors.label)}
							required
							disabled={props.disabled}
							label="名前"
							helperText={touched.label && errors.label}
							value={values.label}
							inputProps={{ 'data-test': 'control-input-label' }}
							onChange={change.bind(null, 'label')}
							fullWidth
						/>
						{!isHp && (
							<>
								<TextField
									name="activePin"
									type="number"
									autoComplete="pf-activePin"
									error={touched.activePin && Boolean(errors.activePin)}
									required
									disabled={props.disabled}
									label={isBool ? '制御ピン(1~16)' : '開けるピン(1~16)'}
									helperText={touched.activePin && errors.activePin}
									value={values.activePin}
									inputProps={{ 'data-test': 'control-input-activePin' }}
									onChange={change.bind(null, 'activePin')}
									fullWidth
								/>
								<TextField
									name="negativePin"
									type="number"
									autoComplete="pf-negativePin"
									error={touched.negativePin && Boolean(errors.negativePin)}
									style={{
										visibility: isBool ? 'hidden' : 'visible',
									}}
									required
									disabled={props.disabled}
									label="閉めるピン"
									helperText={touched.negativePin && errors.negativePin}
									value={values.negativePin}
									inputProps={{ 'data-test': 'control-input-negativePin' }}
									onChange={change.bind(null, 'negativePin')}
									fullWidth
								/>
								<PinIndicator
									usedPins={usedPins}
									twayPins={twayPins}
									house={props.house}
									selectedPins={
										new Set(
											[values.activePin, values.negativePin].filter(
												(p): p is number => p != null,
											),
										)
									}
									onPinClick={(pin) => {
										setFieldValue(
											'activePin',
											values.activePin === pin ? null : pin,
										)
										setFieldTouched('activePin', true, false)
									}}
								/>
							</>
						)}
						{isHp && (
							<TextField
								name="pumpNumber"
								type="number"
								autoComplete="pf-pumpNumber"
								error={touched.pumpNumber && Boolean(errors.pumpNumber)}
								required
								disabled={props.disabled}
								label="ポンプ番号"
								helperText={touched.pumpNumber && errors.pumpNumber}
								value={values.pumpNumber}
								inputProps={{ 'data-test': 'control-input-pumpNumber', min: 1 }}
								onChange={change.bind(null, 'pumpNumber')}
								fullWidth
							/>
						)}
						{isTway && (
							<TextField
								select
								name="relatedHpId"
								autoComplete="pf-relatedHpId"
								error={touched.relatedHpId && Boolean(errors.relatedHpId)}
								disabled={props.disabled}
								label="関連ヒートポンプ（任意）"
								helperText={
									touched.relatedHpId && errors.relatedHpId
										? errors.relatedHpId
										: '関連するHP機器を選択できます'
								}
								value={values.relatedHpId ?? ''}
								inputProps={{ 'data-test': 'control-input-relatedHpId' }}
								onChange={(e) => {
									const value =
										e.target.value === '' ? null : Number(e.target.value)
									setFieldValue('relatedHpId', value)
									setFieldTouched('relatedHpId', true, false)
								}}
								fullWidth
							>
								<MenuItem value="">なし</MenuItem>
								{hpControls.map((hp) => (
									<MenuItem key={hp.id} value={hp.id}>
										{hp.label}
									</MenuItem>
								))}
							</TextField>
						)}
						<TextField
							name="timer"
							type="number"
							autoComplete="pf-timer"
							error={touched.timer && Boolean(errors.timer)}
							required
							disabled={props.disabled || isTway}
							style={{
								visibility: isBool || isHp || isTway ? 'hidden' : 'visible',
							}}
							label="実行時間(秒)"
							helperText={touched.timer && errors.timer}
							value={values.timer}
							inputProps={{
								'data-test': 'control-input-timer',
								min: 1,
							}}
							onChange={change.bind(null, 'timer')}
							fullWidth
						/>
						<TextField
							name="cooltime"
							type="number"
							autoComplete="pf-cooltime"
							error={touched.cooltime && Boolean(errors.cooltime)}
							required
							disabled={props.disabled}
							style={{
								visibility: isBool || isHp || isTway ? 'hidden' : 'visible',
							}}
							label="クールタイム(秒,最低でも空ける実行間隔)"
							helperText={touched.cooltime && errors.cooltime}
							value={values.cooltime}
							inputProps={{ 'data-test': 'control-input-cooltime' }}
							onChange={change.bind(null, 'cooltime')}
							fullWidth
						/>
						<Button
							variant="contained"
							color="primary"
							onClick={() => handleSubmit()}
							disabled={props.disabled}
							size={'large'}
						>
							追加する
						</Button>
					</>
				)}
			</form>
		</div>
	)
}

export default CreateControlForm
